// ============================================================
// src/pages/links.js
//
// ── TWO EXPORTED COMPONENTS ──────────────────────────────────
//
// 1. LinksEditor (default export)
//    Authenticated page at /links.
//    Left panel: color + font controls matching promo.js layout.
//    Right panel: phone-frame live preview.
//    Saves config to public.profiles.links_config (jsonb).
//
// 2. PublicLinks (named export)
//    Public page at /fanlinks/:performerId -- no auth required.
//
// ── ROUTING (add to App.jsx) ─────────────────────────────────
//   import LinksEditor, { PublicLinks } from './pages/links'
//   <Route path="/fanlinks" element={<AuthGuard><LinksEditor /></AuthGuard>} />
//   <Route path="/fanlinks/:performerId" element={<PublicLinks />} />
//
// ── CSS ARCHITECTURE ─────────────────────────────────────────
// All layout and visual styling is in links.css.
// Dynamic theme values are applied as CSS custom properties on
// the .lp-page container via a single style attribute:
//   --lp-bg      page background          (matches promo bgColor)
//   --lp-accent  icons/labels/borders     (matches promo accentColor)
//   --lp-date    show card date text       (matches promo dateColor)
//   --lp-title   performer name + titles  (matches promo textColor)
//   --lp-hfont   header font family
//   --lp-bfont   body font family
// Derived variables (--lp-card, --lp-border, --lp-muted) are
// computed from these in CSS using color-mix() -- no JS needed.
//
// The only inline styles in this file are:
//   1. The six CSS variable assignments on .lp-page (style attr)
//   2. Font preview buttons: fontFamily (dynamic, no class equiv.)
//   3. Preset swatches: backgroundColor (dynamic color values)
//   4. Color picker dot: left + top % (computed canvas position)
//   5. Color picker swatches: backgroundColor (current color)
// ============================================================

import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, Link }                          from 'react-router-dom'
import { supabase }                                 from '../lib/supabaseClient'
import { safeUrl }                                  from '../utils/url'
import './links.css'


// ── FONT DEFINITIONS ─────────────────────────────────────────
// Same lists as promo.js for consistency across the app.
const HEADER_FONTS = [
  { label: 'Fredoka One', value: 'Fredoka One' },
  { label: 'Pacifico',    value: 'Pacifico'    },
  { label: 'New Rocker',  value: 'New Rocker'  },
  { label: 'Bebas Neue',  value: 'Bebas Neue'  },
  { label: 'Teko',        value: 'Teko'        },
  { label: 'Anton',       value: 'Anton'       },
]

const BODY_FONTS = [
  { label: 'Atkinson',  value: 'Atkinson Hyperlegible' },
  { label: 'Open Sans', value: 'Open Sans'             },
  { label: 'Lato',      value: 'Lato'                  },
  { label: 'DM Sans',   value: 'DM Sans'               },
]


// ── COLOR PRESETS ─────────────────────────────────────────────
// Same field names as promo.js: bgColor, accentColor, dateColor,
// titleColor (promo calls it textColor -- we use titleColor here).
const COLOR_PRESETS = [
  {
    id: 'its-pink', label: "It's Pink!",
    bgColor: '#fff5f7', accentColor: '#a8005c',
    dateColor: '#c2185b', titleColor: '#65025e',
    socialColor: '#ff69b4', onSocialColor: '#65025e',
    cardBg: '#ffe4ef', cardText: '#65025e',
    ticketColor: '#c2185b', onTicketColor: '#ffffff',
  },
  {
    id: 'green-gush', label: 'Green Gush',
    bgColor: '#f7fef0', accentColor: '#3a6200',
    dateColor: '#4a7c00', titleColor: '#ffffff',
    socialColor: '#84cc16', onSocialColor: '#1a2600',
    cardBg: '#edffd4', cardText: '#1a2600',
    ticketColor: '#4a7c00', onTicketColor: '#ffffff',
  },
  {
    id: 'blue-shock', label: 'Blue Shock',
    bgColor: '#072aec', accentColor: '#ffd700', dateColor: '#ffd700', titleColor: '#ffffff',
    socialColor: '#ffd700', onSocialColor: '#072aec',
    cardBg: '#0a1fc2', cardText: '#ffffff',
    ticketColor: '#ffd700', onTicketColor: '#072aec',
  },
  {
    id: 'bone', label: 'Bone',
    bgColor: '#121212', accentColor: '#e0e0e0', dateColor: '#bdbdbd', titleColor: '#f0f0f0',
    socialColor: '#e0e0e0', onSocialColor: '#121212',
    cardBg: '#1e1e1e', cardText: '#f0f0f0',
    ticketColor: '#bdbdbd', onTicketColor: '#121212',
  },
]

// Default config = It's Pink preset.
const DEFAULT_CONFIG = {
  bgColor:       '#fff5f7',
  accentColor:   '#000000',
  dateColor:     '#000000',
  titleColor:    '#000000',
  socialColor:   '#ff69b4',
  onSocialColor: '#000000',
  cardBg:        '#ffffff',
  cardText:      '#000000',
  ticketColor:   '#000000',
  onTicketColor: '#ffffff',
  headerFont:    'Fredoka One',
  bodyFont:      'Atkinson Hyperlegible',
}


// ── SOCIAL + TIP PLATFORM DEFINITIONS ────────────────────────
const SOCIAL_PLATFORMS = [
  { key: 'instagram',  label: 'Instagram',   cls: 'instagram', prefix: '@', iconType: 'bi',  icon: 'bi-instagram',  link: h => `https://instagram.com/${h}`   },
  { key: 'twitter_x',  label: 'X (Twitter)', cls: 'twitter',   prefix: '@', iconType: 'bi',  icon: 'bi-twitter-x',  link: h => `https://x.com/${h}`            },
  { key: 'tiktok',     label: 'TikTok',      cls: 'tiktok',    prefix: '@', iconType: 'bi',  icon: 'bi-tiktok',     link: h => `https://tiktok.com/@${h}`      },
  { key: 'bluesky',    label: 'Bluesky',     cls: 'bluesky',   prefix: '@', iconType: 'svg', icon: 'bluesky',       link: h => `https://bsky.app/profile/${h}` },
  { key: 'youtube',    label: 'YouTube',     cls: 'youtube',   prefix: '@', iconType: 'bi',  icon: 'bi-youtube',    link: h => `https://youtube.com/@${h}`     },
  { key: 'patreon',    label: 'Patreon',     cls: 'patreon',   prefix: '',  iconType: 'svg', icon: 'patreon',       link: h => `https://patreon.com/${h}`      },
  { key: 'facebook',   label: 'Facebook',    cls: 'facebook',  prefix: '',  iconType: 'bi',  icon: 'bi-facebook',   link: h => `https://facebook.com/${h}`     },
]

const TIP_PLATFORMS = [
  { key: 'cashapp', label: 'Cash App', cls: 'cashapp', prefix: '$', iconKey: 'cashapp', link: h => `https://cash.app/$${h}` },
  { key: 'venmo',   label: 'Venmo',    cls: 'venmo',   prefix: '@', iconKey: 'venmo',   link: h => `https://venmo.com/${h}` },
  { key: 'paypal',  label: 'PayPal',   cls: 'paypal',  prefix: '',  iconKey: 'paypal',  link: h => `https://paypal.me/${h}` },
]


// ── BRAND SVG ICONS ───────────────────────────────────────────
// Platforms not available in Bootstrap Icons use inline SVGs.
const SVG_ICONS = {
  bluesky: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><path d="M439.8 358.7C436.5 358.3 433.1 357.9 429.8 357.4C433.2 357.8 436.5 358.3 439.8 358.7zM320 291.1C293.9 240.4 222.9 145.9 156.9 99.3C93.6 54.6 69.5 62.3 53.6 69.5C35.3 77.8 32 105.9 32 122.4C32 138.9 41.1 258 47 277.9C66.5 343.6 136.1 365.8 200.2 358.6C203.5 358.1 206.8 357.7 210.2 357.2C206.9 357.7 203.6 358.2 200.2 358.6C106.3 372.6 22.9 406.8 132.3 528.5C252.6 653.1 297.1 501.8 320 425.1C342.9 501.8 369.2 647.6 505.6 528.5C608 425.1 533.7 372.5 439.8 358.6C436.5 358.2 433.1 357.8 429.8 357.3C433.2 357.7 436.5 358.2 439.8 358.6C503.9 365.7 573.4 343.5 593 277.9C598.9 258 608 139 608 122.4C608 105.8 604.7 77.7 586.4 69.5C570.6 62.4 546.4 54.6 483.2 99.3C417.1 145.9 346.1 240.4 320 291.1z"/></svg>`,
  patreon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><path d="M554 217.8C553.9 152.4 503 98.8 443.3 79.5C369.1 55.5 271.3 59 200.4 92.4C114.6 132.9 87.6 221.7 86.6 310.2C85.8 383 93 574.6 201.2 576C281.5 577 293.5 473.5 330.7 423.7C357.1 388.2 391.2 378.2 433.1 367.8C505.1 350 554.2 293.1 554.1 217.8L554 217.8z"/></svg>`,
  cashapp: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><path d="M536.3 160.9C526.9 134.4 506 113.5 479.5 103.9C455.4 96 433.2 96 387.9 96L252 96C207.2 96 184.8 96 160.7 103.5C134.2 113.1 113.3 134 103.7 160.5C96 184.8 96 207.2 96 252.2L96 387.8C96 433 96 455.2 103.5 479.3C113.1 505.8 134 526.7 160.5 536.3C184.8 544 207.2 544 252.1 544L387.8 544C432.8 544 455.2 544 479.4 536.3C505.9 526.7 526.8 505.8 536.4 479.3C544.1 455 544.1 432.6 544.1 387.8L544.1 252.3C544.1 207.3 544.1 184.9 536.4 160.8zM419.1 249.4L393.3 270.5C391 272.4 387.8 272 386 269.6C372.8 253.4 352.3 244.2 329.9 244.2C304.9 244.2 289.3 255.1 289.3 270.4C288.9 283.2 301 290 338.4 298.1C385.6 308.1 407.1 327.8 407.1 360.8C407.1 402.2 373.4 432.7 320.7 436.1L315.6 460.6C315.2 462.9 313 464.7 310.5 464.7L269.9 464.7C266.5 464.7 264.1 461.5 264.8 458.3L271.2 431C245.2 423.5 224 409 211.9 391.3C210.4 389 210.8 386 213 384.3L241.2 362.3C243.5 360.4 247 361 248.7 363.4C263.6 384.3 286.7 396.7 314.4 396.7C339.4 396.7 358.2 384.5 358.2 367C358.2 353.6 348.8 347.4 317 340.8C262.8 329.1 241.2 309 241.2 275.9C241.2 237.5 273.4 208.7 322.1 204.9L327.4 179.5C327.8 177.2 330 175.4 332.5 175.4L372.4 175.4C375.6 175.4 378.2 178.4 377.5 181.6L371.3 210C392.2 216.4 409.3 227.9 420 242.2C421.7 244.3 421.3 247.5 419.1 249.2z"/></svg>`,
  venmo:   `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><path d="M530.5 78.8C547.9 107.5 555.8 137 555.8 174.3C555.8 293.3 453.9 447.8 371.1 556.4L182.2 556.4L106.4 104.9L271.8 89.2L311.9 410.5C349.3 349.7 395.5 254.2 395.5 189.1C395.5 153.5 389.4 129.2 379.8 109.2L530.5 78.8z"/></svg>`,
  paypal:  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><path d="M239.9 359.9C236.4 379.1 222.5 468.6 218.4 493.9C218.1 495.7 217.4 496.4 215.4 496.4L140.8 496.4C133.2 496.4 127.7 489.8 128.7 482.5L187.3 110.6C188.8 101 197.4 93.7 207.3 93.7C359.6 93.7 372.4 90 411.3 105.1C471.4 128.4 476.9 184.6 455.3 245.4C433.8 308 382.8 334.9 315.2 335.7C271.8 336.4 245.7 328.7 239.9 359.9zM485.6 216C483.8 214.7 483.1 214.2 482.6 217.3C480.6 228.7 477.5 239.8 473.8 250.9C433.9 364.7 323.3 354.8 269.3 354.8C263.2 354.8 259.2 358.1 258.4 364.2C235.8 504.6 231.3 533.9 231.3 533.9C230.3 541 234.8 546.8 241.9 546.8L305.4 546.8C314 546.8 321.1 540.5 322.8 531.9C323.5 526.5 321.7 538 337.2 440.6C341.8 418.6 351.5 420.9 366.5 420.9C437.5 420.9 492.9 392.1 509.4 308.6C515.9 273.8 514 237.2 485.6 216z"/></svg>`,
}


// ── HELPERS ───────────────────────────────────────────────────

// formatGigDate -- "Thu, Apr 2, 2026". Local-time construction
// avoids the UTC midnight offset bug that shifts dates back one day.
function formatGigDate(dateStr) {
  if (!dateStr) return ''
  try {
    const [y, m, d] = dateStr.split('T')[0].split('-').map(Number)
    return new Date(y, m - 1, d).toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
    })
  } catch (_) { return dateStr }
}

// formatGigTime -- "9:30 PM" from "21:30" or "21:30:00"
function formatGigTime(t) {
  if (!t) return ''
  const parts = t.split(':').map(Number)
  if (parts.length < 2 || isNaN(parts[0])) return t
  let h = parts[0]
  const m    = String(parts[1] ?? 0).padStart(2, '0')
  const ampm = h >= 12 ? 'PM' : 'AM'
  if (h === 0) h = 12; else if (h > 12) h -= 12
  return `${h}:${m} ${ampm}`
}

// sortGigsByDateAndTime -- gigs are fetched with .order('date') only,
// which has no secondary key, so gigs sharing the same date keep
// whatever order Supabase happens to return them in. This sorts
// ascending by date first, then by time (falling back to show
// time) so gigs on the same day still display earliest-first.
function sortGigsByDateAndTime(gigList) {
  return [...gigList].sort((a, b) => {
    if (a.date !== b.date) return a.date < b.date ? -1 : 1
    // date strings are "YYYY-MM-DD", so plain string comparison
    // sorts chronologically without needing to parse them
    return (a.time || '').localeCompare(b.time || '')
  })
}

// buildVenueSegments -- same pattern as promo.js's buildSegments().
// Returns an array of non-empty strings to join with ' | '.
function buildVenueSegments(gig) {
  const s = []
  if (gig.venue_name)                         s.push(gig.venue_name)
  if (gig.city && gig.state)                  s.push(`${gig.city}, ${gig.state}`)
  else if (gig.city)                          s.push(gig.city)
  const t = formatGigTime(gig.time); if (t)   s.push(t)
  if (gig.age_restriction)                    s.push(gig.age_restriction)
  if (gig.cover_charge)                       s.push(gig.cover_charge)
  return s
}

// injectGoogleFonts -- inserts a <link> tag exactly once.
function injectGoogleFonts() {
  const ID = 'links-page-google-fonts'
  if (document.getElementById(ID)) return
  const link = document.createElement('link')
  link.id   = ID
  link.rel  = 'stylesheet'
  link.href =
    'https://fonts.googleapis.com/css2?' +
    'family=Fredoka+One&family=Pacifico&family=New+Rocker&' +
    'family=Bebas+Neue&family=Teko:wght@700&family=Anton&' +
    'family=Atkinson+Hyperlegible:ital,wght@0,400;0,700;1,400&' +
    'family=Open+Sans:wght@400;700&family=Lato:wght@400;700&' +
    'family=DM+Sans:wght@400;700&display=swap'
  document.head.appendChild(link)
}


// ============================================================
// ColorPicker
// Ported from promo.js with CSS classes replacing inline styles.
// See links.css .lcp-* for all layout declarations.
// The three unavoidable inline styles are documented at each site.
// ============================================================
function ColorPicker({ label, value, onChange, pickerId, activeId, setActiveId }) {
  const isOpen    = activeId === pickerId
  const canvasRef = useRef(null)

  // ── Color conversion (identical to promo.js) ──────────────
  const hexToHsv = useCallback((hex) => {
    const full = hex.replace(/^#([a-f\d])([a-f\d])([a-f\d])$/i,
      (_, r, g, b) => '#' + r + r + g + g + b + b)
    const res = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(full)
    if (!res) return { h: 0, s: 1, v: 1 }
    let r = parseInt(res[1], 16) / 255
    let g = parseInt(res[2], 16) / 255
    let b = parseInt(res[3], 16) / 255
    const max = Math.max(r,g,b), min = Math.min(r,g,b), d = max - min
    let h = 0
    const s = max === 0 ? 0 : d / max, v = max
    if (max !== min) {
      if (max === r)      h = ((g-b)/d + (g<b?6:0)) / 6
      else if (max === g) h = ((b-r)/d + 2) / 6
      else                h = ((r-g)/d + 4) / 6
    }
    return { h: h * 360, s, v }
  }, [])

  const hsvToHex = useCallback((h, s, v) => {
    const f   = n => { const k=(n+h/60)%6; return v - v*s*Math.max(0,Math.min(k,4-k,1)) }
    const toH = x => Math.round(x*255).toString(16).padStart(2,'0')
    return `#${toH(f(5))}${toH(f(3))}${toH(f(1))}`
  }, [])

  const hsv = hexToHsv(value)
  const [hue,      setHue]      = useState(hsv.h)
  const [localHex, setLocalHex] = useState(value)

  useEffect(() => { setLocalHex(value) }, [value])

  // Redraw 2D SV canvas whenever hue or open state changes
  useEffect(() => {
    if (!isOpen) return
    const canvas = canvasRef.current; if (!canvas) return
    const ctx = canvas.getContext('2d'), W = canvas.width, H = canvas.height
    const gradH = ctx.createLinearGradient(0,0,W,0)
    gradH.addColorStop(0, '#fff')
    gradH.addColorStop(1, `hsl(${hue},100%,50%)`)
    ctx.fillStyle = gradH; ctx.fillRect(0,0,W,H)
    const gradV = ctx.createLinearGradient(0,0,0,H)
    gradV.addColorStop(0, 'rgba(0,0,0,0)')
    gradV.addColorStop(1, 'rgba(0,0,0,1)')
    ctx.fillStyle = gradV; ctx.fillRect(0,0,W,H)
  }, [hue, isOpen])

  const pickFromCanvas = useCallback(e => {
    const canvas = canvasRef.current; if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    const cx = e.clientX ?? e.touches?.[0]?.clientX
    const cy = e.clientY ?? e.touches?.[0]?.clientY
    const x  = Math.max(0, Math.min(1, (cx - rect.left) / rect.width))
    const y  = Math.max(0, Math.min(1, (cy - rect.top)  / rect.height))
    const hex = hsvToHex(hue, x, 1 - y)
    onChange(hex); setLocalHex(hex)
  }, [hue, onChange, hsvToHex])

  const onCanvasDown = e => {
    e.preventDefault(); pickFromCanvas(e)
    const onMove = me => pickFromCanvas(me)
    const onUp   = () => { window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerup', onUp) }
    window.addEventListener('pointermove', onMove); window.addEventListener('pointerup', onUp)
  }

  const onHueChange = e => {
    const h = parseFloat(e.target.value); setHue(h)
    const c = hexToHsv(value); const hex = hsvToHex(h, c.s, c.v)
    onChange(hex); setLocalHex(hex)
  }

  const onHexChange = e => {
    const v = e.target.value; setLocalHex(v)
    if (/^#[0-9a-fA-F]{6}$/.test(v)) { onChange(v); setHue(hexToHsv(v).h) }
  }

  const cur  = hexToHsv(value)
  const dotX = cur.s * 100
  const dotY = (1 - cur.v) * 100

  return (
    <div className="lcp-wrapper">

      {/* Trigger: label + swatch + hex + chevron */}
      <button
        type="button"
        className="lcp-trigger"
        onClick={() => setActiveId(isOpen ? null : pickerId)}
      >
        <span className="lcp-label">{label}</span>
        <div className="lcp-trigger-right">
          {/* [inline style 1/5] swatch backgroundColor = current color value */}
          <div className="lcp-swatch" style={{ backgroundColor: value }} />
          <span className="lcp-hex-text">{value}</span>
          <i className={`bi bi-chevron-${isOpen ? 'up' : 'down'} lcp-chevron`} />
        </div>
      </button>

      {/* Popover */}
      {isOpen && (
        <div className="lcp-popover">

          {/* 2D SV canvas */}
          <div className="lcp-canvas-wrap" onPointerDown={onCanvasDown}>
            <canvas ref={canvasRef} width={170} height={110} className="lcp-canvas" />
            {/* [inline style 2/5 + 3/5] dot left + top = computed % positions;
                backgroundColor = current color. All three are computed values
                with no CSS class equivalent. */}
            <div
              className="lcp-dot"
              style={{ left: `${dotX}%`, top: `${dotY}%`, backgroundColor: value }}
            />
          </div>

          {/* Hue slider */}
          <input
            type="range"
            min="0" max="360" step="1"
            value={hue}
            onChange={onHueChange}
            className="lcp-hue"
          />

          {/* Hex row */}
          <div className="lcp-hex-row">
            {/* [inline style 4/5] hex swatch backgroundColor = current color */}
            <div className="lcp-hex-swatch" style={{ backgroundColor: value }} />
            <input
              type="text"
              value={localHex}
              maxLength={7}
              onChange={onHexChange}
              className="lcp-hex-input"
              placeholder="#000000"
            />
          </div>

        </div>
      )}
    </div>
  )
}


// ============================================================
// LinksPageContent
// Shared presentational component used by both LinksEditor
// (preview) and PublicLinks (public page).
// ============================================================
function LinksPageContent({ profile, gigs, config, isPreview }) {
  const cfg = { ...DEFAULT_CONFIG, ...config }

  // Map profile columns to SOCIAL_PLATFORMS keys
  const socialData = {
    instagram: profile.instagram      || '',
    twitter_x: profile.twitter_handle || '',
    tiktok:    profile.tiktok         || '',
    bluesky:   profile.bluesky        || '',
    youtube:   profile.youtube        || '',
    patreon:   profile.patreon        || '',
    facebook:  profile.facebook_name  || '',
  }

  const tipData = {
    cashapp: profile.cashapp || '',
    venmo:   profile.venmo   || '',
    paypal:  profile.paypal  || '',
  }

  const activeSocials = SOCIAL_PLATFORMS.filter(p => socialData[p.key]?.trim())
  const activeTips    = TIP_PLATFORMS.filter(p => tipData[p.key]?.trim())

  // The ONLY style attribute in this component -- CSS vars only.
  // [inline style 5/5]
  const themeVars = {
    '--lp-bg':        cfg.bgColor,
    '--lp-accent':    cfg.accentColor,
    '--lp-date':      cfg.dateColor,
    '--lp-title':     cfg.titleColor,
    '--lp-social':    cfg.socialColor,
    '--lp-on-social': cfg.onSocialColor,
    '--lp-card-bg':   cfg.cardBg,
    '--lp-card-text': cfg.cardText,
    '--lp-ticket':    cfg.ticketColor,
    '--lp-on-ticket': cfg.onTicketColor,
    '--lp-hfont':     `'${cfg.headerFont}', cursive, sans-serif`,
    '--lp-bfont':     `'${cfg.bodyFont}', sans-serif`,
  }

  return (
    <div
      className={`lp-page${isPreview ? ' lp-page--preview' : ''}`}
      style={themeVars}
    >

      <div className="lp-inner">

        {/* ── HEADER ── */}
        <div className="lp-header">
          <h1 className="lp-name">{profile.performer_name || 'Performer'}</h1>
          {profile.pronouns && <p className="lp-pronouns">{profile.pronouns}</p>}
          {profile.location && (
            <p className="lp-location">
              <svg className="lp-location-icon" width="12" height="12" viewBox="0 0 640 640">
                <path d="M320 64C214.6 64 129 149.6 129 255C129 394.8 320 576 320 576C320 576 511 394.8 511 255C511 149.6 425.4 64 320 64ZM320 329C278.7 329 245 295.3 245 254C245 212.7 278.7 179 320 179C361.3 179 395 212.7 395 254C395 295.3 361.3 329 320 329Z"/>
              </svg>
              {profile.location}
            </p>
          )}
        </div>

        <div className="lp-content">

          {/* ── FIND ME ONLINE ── */}
          {activeSocials.length > 0 && (
            <section className="lp-section">
              <p className="lp-section-title">Follow {profile.performer_name} Online!</p>
              <div className="lp-social-grid">
                {activeSocials.map(plat => {
                  const handle = socialData[plat.key].trim()
                  return (
                    <a
                      key={plat.key}
                      href={plat.link(handle)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`lp-social-btn lp-social-btn--${plat.cls}`}
                    >
                      <span className="lp-social-icon">
                        {plat.iconType === 'bi'
                          ? <i className={`bi ${plat.icon}`} />
                          : <span dangerouslySetInnerHTML={{ __html: SVG_ICONS[plat.icon] }} />
                        }
                      </span>
                      <span className="lp-social-label">{plat.prefix}{handle}</span>
                      <svg className="lp-external-icon" viewBox="0 0 640 640">
                        <path d="M544 288L544 96L352 96L352 160L416 160L224 352L272 400L464 208L464 288ZM512 512L128 512L128 128L320 128L320 64L64 64L64 576L576 576L576 320L512 320Z"/>
                      </svg>
                    </a>
                  )
                })}
              </div>
            </section>
          )}

          {/* ── UPCOMING SHOWS ── */}
          {gigs.length > 0 && (
            <section className="lp-section">
              <p className="lp-section-title">Upcoming Shows</p>
              <div className="lp-shows-list">
                {gigs.map(gig => {
                  const segments = buildVenueSegments(gig)
                  return (
                    <div key={gig.id} className="lp-show-card">
                      {/* Date -- .lp-show-date uses --lp-date */}
                      <div className="lp-show-date">{formatGigDate(gig.date)}</div>
                      {/* Show name -- .lp-show-name uses --lp-title */}
                      <div className="lp-show-name">{gig.name || gig.venue_name}</div>
                      {/* Venue segments */}
                      {segments.length > 0 && (
                        <div className="lp-show-venue">{segments.join(' | ')}</div>
                      )}
                      {/* Ticket button -- only when ticket_link is set */}
                      {gig.ticket_link && (
                        <div className="lp-show-footer">
                          <a
                            href={safeUrl(gig.ticket_link)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="lp-ticket-btn"
                          >
                            Get Tickets
                          </a>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </section>
          )}

          {/* ── WEBSITE ── */}
          {profile.website && (
            <section className="lp-section">
              <p className="lp-section-title">{profile.performer_name}'s Website</p>
              <a
                href={safeUrl(profile.website)}
                target="_blank"
                rel="noopener noreferrer"
                className="lp-website-btn"
              >
                <svg className="lp-website-icon" viewBox="0 0 640 640">
                  <path d="M320 64C178.6 64 64 178.6 64 320C64 461.4 178.6 576 320 576C461.4 576 576 461.4 576 320C576 178.6 461.4 64 320 64ZM320 128C331.9 128 357.3 153.9 376.5 212H263.5C282.7 153.9 308.1 128 320 128ZM252 256L388 256C392.2 278.4 394.5 299 394.5 320C394.5 341 392.2 361.6 388 384L252 384C247.8 361.6 245.5 341 245.5 320C245.5 299 247.8 278.4 252 256ZM205 256L128.8 256C139.2 219.5 160.1 187 188.5 161.5C197.5 199.5 202.3 228.6 205 256ZM263.5 428L376.5 428C357.3 486.1 331.9 512 320 512C308.1 512 282.7 486.1 263.5 428ZM435 256C437.7 228.6 442.5 199.5 451.5 161.5C479.9 187 500.8 219.5 511.2 256ZM435 384L511.2 384C500.8 420.5 479.9 453 451.5 478.5C442.5 440.5 437.7 411.4 435 384Z"/>
                </svg>
                {profile.website.replace(/^https?:\/\//, '')}
              </a>
            </section>
          )}

          {/* ── TIPPING ── */}
          {(activeTips.length > 0 || profile.zelle) && (
            <section className="lp-section">
              <p className="lp-section-title">Digital Tipping</p>
              <p className="lp-tip-note">Show {profile.performer_name} some love with a dollar.</p>
              {activeTips.length > 0 && (
                <div className="lp-tips-list">
                  {activeTips.map(plat => {
                    const handle = tipData[plat.key].trim()
                    return (
                      <a
                        key={plat.key}
                        href={plat.link(handle)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`lp-tip-btn lp-tip-btn--${plat.cls}`}
                      >
                        <span className="lp-tip-icon" dangerouslySetInnerHTML={{ __html: SVG_ICONS[plat.iconKey] }} />
                        <span className="lp-tip-content">
                          <span className="lp-tip-platform">{plat.label}</span>
                          <span className="lp-tip-handle">{plat.prefix}{handle}</span>
                        </span>
                        <svg className="lp-external-icon" viewBox="0 0 640 640">
                          <path d="M544 288L544 96L352 96L352 160L416 160L224 352L272 400L464 208L464 288ZM512 512L128 512L128 128L320 128L320 64L64 64L64 576L576 576L576 320L512 320Z"/>
                        </svg>
                      </a>
                    )
                  })}
                </div>
              )}
            </section>
          )}

        </div>
      </div>

      <div className="lp-inner">
        <footer className="lp-footer">
          Support Local Drag!
        </footer>
      </div>

    </div>
  )
}


// ============================================================
// LinksEditor
// Authenticated page at /links (default export).
// Controls layout matches promo.js:
//   Left column (380px): share URL + presets + bg & colors + fonts
//   Right column (sticky): phone-frame live preview
// Mobile: tab switcher toggles between Edit and Preview columns.
// ============================================================
export default function LinksEditor() {

  const [config,  setConfig]  = useState(DEFAULT_CONFIG)
  const [profile, setProfile] = useState(null)
  const [gigs,    setGigs]    = useState([])
  const [userId,  setUserId]  = useState('')
  const [loading, setLoading] = useState(true)
  const [saving,  setSaving]  = useState(false)
  const [saved,   setSaved]   = useState(false)
  const [copied,  setCopied]  = useState(false)

  // ── Mobile tab toggle ─────────────────────────────────────
  // On narrow viewports the editor shows either the controls OR
  // the preview. The Edit tab is the default.
  const [mobileTab, setMobileTab] = useState('edit') // 'edit' | 'preview'

  // Tracks which ColorPicker popover is open (null = all closed)
  const [activeColorPicker, setActiveColorPicker] = useState(null)
  const pickerAreaRef = useRef(null)

  // Close pickers on outside click
  useEffect(() => {
    const handler = e => {
      if (pickerAreaRef.current && !pickerAreaRef.current.contains(e.target)) {
        setActiveColorPicker(null)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  useEffect(() => {
    injectGoogleFonts()
    loadData()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  async function loadData() {
    setLoading(true)
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) { setLoading(false); return }
    setUserId(user.id)

    const { data: prof } = await supabase
      .from('profiles')
      .select('id, username, performer_name, pronouns, location, instagram, twitter_handle, facebook_name, cashapp, venmo, paypal, website, tiktok, bluesky, youtube, patreon, links_config')
      .eq('id', user.id)
      .maybeSingle()

    if (prof) {
      setProfile(prof)
      if (prof.links_config) setConfig(prev => ({ ...prev, ...prof.links_config }))
    }

    // Upcoming public gigs for the preview
    const today = new Date()
    const todayStr = [today.getFullYear(), String(today.getMonth()+1).padStart(2,'0'), String(today.getDate()).padStart(2,'0')].join('-')
    const { data: upcoming } = await supabase
      .from('gigs')
      .select('id, name, venue_name, date, time, city, state, age_restriction, cover_charge, ticket_link, is_private')
      .eq('user_id', user.id)
      .eq('is_private', false)
      .gte('date', todayStr)
      .order('date', { ascending: true })
      .limit(6)

    setGigs(sortGigsByDateAndTime(upcoming || []))
    setLoading(false)
  }

  const saveConfig = useCallback(async () => {
    if (!userId) return
    setSaving(true); setSaved(false)
    const { error } = await supabase
      .from('profiles')
      .update({ links_config: config })
      .eq('id', userId)
    setSaving(false)
    if (!error) { setSaved(true); setTimeout(() => setSaved(false), 3000) }
  }, [config, userId])

  // Spreading only the color keys (not fonts) keeps heading/body font choices intact.
  const applyPreset = useCallback(preset => {
    const { id, label, ...colors } = preset
    setConfig(prev => ({ ...prev, ...colors }))
  }, [])

  const copyUrl = useCallback(() => {
    const linkSlug = profile?.username || userId
    const url = `${window.location.origin}/fanlinks/${linkSlug}`
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(url)
    else {
      const el = document.createElement('textarea')
      el.value = url; document.body.appendChild(el); el.select(); document.execCommand('copy'); document.body.removeChild(el)
    }
    setCopied(true); setTimeout(() => setCopied(false), 2000)
  }, [profile, userId])

  const linkSlug = profile?.username || userId
  const shareLinkUrl = linkSlug ? `${window.location.origin}/fanlinks/${linkSlug}` : ''

  const isPresetActive = p =>
    config.bgColor     === p.bgColor     &&
    config.accentColor === p.accentColor &&
    config.dateColor   === p.dateColor   &&
    config.titleColor  === p.titleColor  &&
    config.socialColor === p.socialColor &&
    config.cardBg      === p.cardBg      &&
    config.cardText    === p.cardText    &&
    config.ticketColor === p.ticketColor

  if (loading) {
    return (
      <div className="le-page">
        <h1 className="le-page-title">Fan Links Page</h1>
        <p>Loading...</p>
      </div>
    )
  }

  return (
    <div className="le-page">

      <h1 className="le-page-title">Fan Links Page</h1>
      <p className="le-page-sub">
        Customize your Fan Links page and share it as your bio link, so your fans can follow you, see your upcoming shows, and digitally tip you. To help the share url be easier to read, set your username in <Link to="/settings">Settings</Link>
      </p>

      {/* ── Mobile tab switcher -- hidden on desktop via CSS ── */}
      <div className="le-mobile-tabs">
        <button
          type="button"
          className={`le-mobile-tab${mobileTab === 'edit' ? ' le-mobile-tab--active' : ''}`}
          onClick={() => setMobileTab('edit')}
        >
          <i className="bi bi-pencil-square me-1" />Edit
        </button>
        <button
          type="button"
          className={`le-mobile-tab${mobileTab === 'preview' ? ' le-mobile-tab--active' : ''}`}
          onClick={() => setMobileTab('preview')}
        >
          <i className="bi bi-eye me-1" />Preview
        </button>
      </div>

      <div className="le-layout">

        {/* ════════════════════════════════════════════════════
            CONTROLS COLUMN
            Hidden on mobile when preview tab is active.
        ════════════════════════════════════════════════════ */}
        <div className={`le-controls-col${mobileTab === 'preview' ? ' le-col--hidden-mobile' : ''}`}>


          {/* ── Share URL ── */}
          <div className="card mb-3">
            <div className="card-header">
              <i className="bi bi-share-fill" />Your Link
            </div>
            <div className="card-body">
              <div className="le-share-bar">
                <div className="le-url-display" title={shareLinkUrl}>{shareLinkUrl}</div>
                <button className="le-copy-btn" onClick={copyUrl} type="button">
                  {copied
                    ? <><i className="bi bi-check-lg me-1" />Copied!</>
                    : <><i className="bi bi-clipboard me-1" />Copy</>}
                </button>
                <a className="le-open-btn" href={shareLinkUrl} target="_blank" rel="noopener noreferrer">
                  <i className="bi bi-box-arrow-up-right" />
                </a>
              </div>
              <p className="le-share-hint">
                Use this as your bio link on Instagram, TikTok, etc.
                Add socials and payment handles and <strong>set your username in <Link to="/settings">Settings</Link>.</strong>
              </p>
            </div>
          </div>


          {/* ── Background + Colors ── */}
          <div className="card mb-3">
            <div className="card-header">
              <i className="bi bi-palette me-1" />Background &amp; Colors
            </div>
            <div className="card-body" ref={pickerAreaRef}>

              {/* Preset row */}
              <div className="le-presets-row">
                {COLOR_PRESETS.map(preset => (
                  <button
                    key={preset.id}
                    type="button"
                    className={`le-preset-btn${isPresetActive(preset) ? ' le-preset-btn--active' : ''}`}
                    onClick={() => applyPreset(preset)}
                  >
                    {/* [inline style] swatch/dot backgroundColor = dynamic color value */}
                    <div className="le-preset-swatch" style={{ backgroundColor: preset.bgColor }}>
                      <div className="le-preset-dot" style={{ backgroundColor: preset.accentColor }} />
                    </div>
                    {preset.label}
                  </button>
                ))}
              </div>

              {/* Two-col split: Web Page | Gig Card */}
              <div className="le-split">

                {/* Left: Web Page */}
                <div className="le-split-col">
                  <p className="le-split-col-label">Web Page</p>
                  <div className="le-bg-row">
                    <input
                      type="color"
                      className="form-control form-control-color le-bg-input"
                      value={config.bgColor}
                      onChange={e => setConfig(prev => ({ ...prev, bgColor: e.target.value }))}
                    />
                    <span className="le-bg-hex">Background</span>
                  </div>
                  <ColorPicker
                    label="Headings"
                    value={config.titleColor}
                    onChange={v => setConfig(prev => ({ ...prev, titleColor: v }))}
                    pickerId="titles"
                    activeId={activeColorPicker}
                    setActiveId={setActiveColorPicker}
                  />
                  <ColorPicker
                    label="Social BG"
                    value={config.socialColor}
                    onChange={v => setConfig(prev => ({ ...prev, socialColor: v }))}
                    pickerId="socials"
                    activeId={activeColorPicker}
                    setActiveId={setActiveColorPicker}
                  />
                  <ColorPicker
                    label="Social Text"
                    value={config.onSocialColor}
                    onChange={v => setConfig(prev => ({ ...prev, onSocialColor: v }))}
                    pickerId="onSocialColor"
                    activeId={activeColorPicker}
                    setActiveId={setActiveColorPicker}
                  />
                  <ColorPicker
                    label="Accent"
                    value={config.accentColor}
                    onChange={v => setConfig(prev => ({ ...prev, accentColor: v }))}
                    pickerId="accent"
                    activeId={activeColorPicker}
                    setActiveId={setActiveColorPicker}
                  />
                </div>

                <div className="le-divider-v" />

                {/* Right: Gig Card */}
                <div className="le-split-col">
                  <p className="le-split-col-label">Gig Card</p>
                  <ColorPicker
                    label="Card BG"
                    value={config.cardBg}
                    onChange={v => setConfig(prev => ({ ...prev, cardBg: v }))}
                    pickerId="cardBg"
                    activeId={activeColorPicker}
                    setActiveId={setActiveColorPicker}
                  />
                  <ColorPicker
                    label="Card Font"
                    value={config.cardText}
                    onChange={v => setConfig(prev => ({ ...prev, cardText: v }))}
                    pickerId="cardText"
                    activeId={activeColorPicker}
                    setActiveId={setActiveColorPicker}
                  />
                  <ColorPicker
                    label="Dates"
                    value={config.dateColor}
                    onChange={v => setConfig(prev => ({ ...prev, dateColor: v }))}
                    pickerId="dates"
                    activeId={activeColorPicker}
                    setActiveId={setActiveColorPicker}
                  />
                  <ColorPicker
                    label="Ticket Btn"
                    value={config.ticketColor}
                    onChange={v => setConfig(prev => ({ ...prev, ticketColor: v }))}
                    pickerId="ticketColor"
                    activeId={activeColorPicker}
                    setActiveId={setActiveColorPicker}
                  />
                  <ColorPicker
                    label="Ticket Text"
                    value={config.onTicketColor}
                    onChange={v => setConfig(prev => ({ ...prev, onTicketColor: v }))}
                    pickerId="onTicketColor"
                    activeId={activeColorPicker}
                    setActiveId={setActiveColorPicker}
                  />
                </div>

              </div>

            </div>
          </div>


          {/* ── Fonts ── */}
          <div className="card mb-3">
            <div className="card-header">
              <i className="bi bi-fonts me-1" />Fonts
            </div>
            <div className="card-body">
              <div className="le-split">

                <div className="le-split-col">
                  <p className="le-split-col-label">Heading</p>
                  {HEADER_FONTS.map(f => (
                    <button
                      key={f.value}
                      type="button"
                      className={`le-font-btn${config.headerFont === f.value ? ' le-font-btn--active' : ''}`}
                      // fontFamily preview -- unavoidable inline style
                      style={{ fontFamily: `'${f.value}', cursive, sans-serif` }}
                      onClick={() => setConfig(prev => ({ ...prev, headerFont: f.value }))}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>

                <div className="le-divider-v" />

                <div className="le-split-col">
                  <p className="le-split-col-label">Body</p>
                  {BODY_FONTS.map(f => (
                    <button
                      key={f.value}
                      type="button"
                      className={`le-font-btn${config.bodyFont === f.value ? ' le-font-btn--active' : ''}`}
                      // fontFamily preview -- unavoidable inline style
                      style={{ fontFamily: `'${f.value}', sans-serif` }}
                      onClick={() => setConfig(prev => ({ ...prev, bodyFont: f.value }))}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>

              </div>
            </div>
          </div>


          {/* ── Save ── */}
          <div className="card mb-3">
            <div className="card-body">
              <div className="le-save-bar">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={saveConfig}
                  disabled={saving}
                >
                  {saving
                    ? <><span className="spinner-border spinner-border-sm me-2" />Saving...</>
                    : <><i className="bi bi-floppy me-1" />Save Theme</>
                  }
                </button>
                {saved && (
                  <span className="le-save-ok">
                    <i className="bi bi-check-circle-fill" /> Saved.
                  </span>
                )}
              </div>
            </div>
          </div>


        </div>{/* end controls column */}


        {/* ════════════════════════════════════════════════════
            PREVIEW COLUMN
            Sticky on desktop; full-width on mobile when
            the Preview tab is active.
        ════════════════════════════════════════════════════ */}
        <div className={`le-preview-col${mobileTab === 'edit' ? ' le-col--hidden-mobile' : ''}`}>
          <p className="le-preview-label">Live Preview</p>
          <div className="le-preview-frame">
            {profile ? (
              <LinksPageContent
                profile={profile}
                gigs={gigs}
                config={config}
                isPreview={true}
              />
            ) : (
              <div className="lp-loading">
                <div className="lp-spinner" />
                <p className="lp-loading-text">Loading preview...</p>
              </div>
            )}
          </div>
          <p className="form-text mt-2 text-center">
            Fan Links Page only shows upcoming public gigs. Edit gig details to set a gig to private.
          </p>
        </div>

      </div>{/* end layout */}

    </div>
  )
}


// ============================================================
// PublicLinks
// Public page at /fanlinks/:performerId -- no auth required.
// ============================================================
export function PublicLinks() {
  const { performerId } = useParams()
  const isUUID = /^[0-9a-f-]{36}$/.test(performerId)

  const [profile,  setProfile]  = useState(null)
  const [gigs,     setGigs]     = useState([])
  const [config,   setConfig]   = useState(DEFAULT_CONFIG)
  const [loading,  setLoading]  = useState(true)
  const [notFound, setNotFound] = useState(false)

  useEffect(() => {
    injectGoogleFonts()
    if (!performerId) { setNotFound(true); setLoading(false); return }
    fetchData()
  }, [performerId]) // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchData() {
    setLoading(true)
    const { data: prof, error } = await supabase
      .from('profiles')
      .select('id, username, performer_name, pronouns, location, instagram, twitter_handle, facebook_name, cashapp, venmo, paypal, website, tiktok, bluesky, youtube, patreon, links_config')
      .eq(isUUID ? 'id' : 'username', performerId)
      .maybeSingle()

    if (error || !prof) { setNotFound(true); setLoading(false); return }
    setProfile(prof)
    if (prof.links_config) setConfig(prev => ({ ...prev, ...prof.links_config }))

    const profileId = prof.id
    const today     = new Date()
    const todayStr  = [today.getFullYear(), String(today.getMonth()+1).padStart(2,'0'), String(today.getDate()).padStart(2,'0')].join('-')
    const { data: upcoming } = await supabase
      .from('gigs')
      .select('id, name, venue_name, date, time, city, state, age_restriction, cover_charge, ticket_link, is_private')
      .eq('user_id', profileId)
      .eq('is_private', false)
      .gte('date', todayStr)
      .order('date', { ascending: true })
      .limit(20)

    setGigs(sortGigsByDateAndTime(upcoming || []))
    setLoading(false)
  }

  if (loading) {
    return (
      <div className="lp-loading">
        <div className="lp-spinner" />
        <p className="lp-loading-text">Loading...</p>
      </div>
    )
  }

  if (notFound || !profile) {
    return (
      <div className="lp-not-found">
        <h1 className="lp-not-found-title">Performer Not Found</h1>
        <p className="lp-not-found-sub">This link may be invalid or the performer has deactivated their page.</p>
      </div>
    )
  }

  return (
    <LinksPageContent
      profile={profile}
      gigs={gigs}
      config={config}
      isPreview={false}
    />
  )
}